import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { useGetClientRepresentative } from 'hooks/useGetClientRepresentative';
import { ZfBox, ZfInput, ZfLoadingSpinner, ZfSelectOption, ZfTypography } from '@zafire/react';
import { format, isValid, parse } from 'date-fns';
import { GlobalState } from 'reducers/stateTypes';

/**
 * AgGrid Custom Filter Props Interface
 */
interface IPartnerColumnFilterProps {
  filterChangedCallback?: () => void;
  api?: any;
  columnApi?: any;
  column?: any;
  registerFilter?: (filterInstance: any) => void;
}

/**
 * Custom AgGrid column filter for Partner selection
 * Uses PSH API for partner search (same as creation screen)
 * Provides server-side searchable dropdown with debounced search
 */

const DROPDOWN_HEIGHT = 32; // Increased height for detailed labels with DOI and Incorporation
export const PartnerColumnFilter = forwardRef<any, IPartnerColumnFilterProps>((props, ref) => {
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [selectedPartnerLabel, setSelectedPartnerLabel] = useState<string>('');
  const [debouncedLabel, setDebouncedLabel] = useState('');
  const [clientRepresentativeSuggestion, setClientRepresentativeSuggestion] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const { t } = useTranslation();

  // Register filter instance with parent component
  useEffect(() => {
    if (props.registerFilter) {
      // Pass the current component instance (forwardRef creates a ref object)
      props.registerFilter({
        setModel: (model: any) => {
          if (model === null || model === undefined) {
            setSelectedPartner('');
            setClientRepresentativeSuggestion('');
            setDebouncedLabel('');
          }
        },
      });
    }
  }, [props]);

  // Update dropdown position when showing
  useEffect(() => {
    if (clientRepresentativeSuggestion.length > 0 && inputRef.current && !selectedPartner) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 600), // Minimum 600px to show full partner details (name, bizKey, DOI, incorporation)
      });
    } else {
      setDropdownPosition(null);
    }
  }, [clientRepresentativeSuggestion, selectedPartner]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLabel(clientRepresentativeSuggestion);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [clientRepresentativeSuggestion]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        inputRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setClientRepresentativeSuggestion('');
      }
    };

    if (dropdownPosition) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [dropdownPosition]);

  // Fetch partners based on search query using PSH API
  const {
    data: partnerResponse,
    isFetching: partnersLoading,
    isSuccess: partnersSuccess,
  } = useGetClientRepresentative(debouncedLabel);

  // Transform PSH API response to dropdown format with detailed information
  const partnerList = useMemo(() => {
    if (!partnerResponse?.hits) return [];
    return partnerResponse.hits.map((item) => {
      let doLabel = '',
        domLabel = '',
        formattedDate = '';
      if (item.dob) {
        const parsedDate = parse(item.dob, 'yyyyMMdd', new Date());
        if (isValid(parsedDate)) {
          formattedDate = format(parsedDate, 'dd.MM.yyyy');
        }
      }
      const country = countryOptions.find((obj) => obj.value === item.mainDomicileCountry);
      if (item.type === 'PI') {
        doLabel = `DOB: ${formattedDate || 'null'}`;

        domLabel = `Domicile: ${country?.label || 'null'}`;
      } else {
        doLabel = `DOI: ${formattedDate || 'null'}`;
        domLabel = `Incorporation: ${country?.label || 'null'}`;
      }
      return {
        label: `${item.name} (${item.bizKey}) | ${doLabel} | ${domLabel}`,
        value: item.uuid,
      };
    });
  }, [partnerResponse, countryOptions]);

  // Ensure selected partner is always in options (even if not in current search results)
  // const optionsWithSelected = React.useMemo(() => {
  //   if (!selectedPartner) return partnerList ?? [];

  //   const options = partnerList ?? [];
  //   const alreadyInList = options.some((p) => p.value === selectedPartner.value);

  //   if (alreadyInList) {
  //     return options;
  //   } else {
  //     // Add selected partner to top of list
  //     return [selectedPartner, ...options];
  //   }
  // }, [partnerList, selectedPartner]);

  // AgGrid filter interface methods
  useImperativeHandle(ref, () => {
    return {
      // Called by AgGrid when it needs to know if filter is active
      isFilterActive() {
        return selectedPartner !== null && selectedPartner !== '';
      },

      // Called by AgGrid to get the filter model
      getModel() {
        if (selectedPartner === '' || selectedPartner === null) {
          return null;
        }
        return {
          filterType: 'text',
          type: 'equals',
          filter: selectedPartner, // Partner UUID
        };
      },

      // Called by AgGrid to set the filter model (e.g., on filter restore or when reopening)
      setModel(model: any) {
        if (model === null || model === undefined) {
          setSelectedPartner('');
          setClientRepresentativeSuggestion(''); // Clear search input
          setDebouncedLabel(''); // Clear debounced search as well
        } else {
          // Restore selected partner from model
          if (model.filter) {
            // Try to find partner in current list
            const partner = partnerList?.find((p) => p.value === model.filter);
            if (partner) {
              setSelectedPartner(partner.value);
            } else {
              setSelectedPartner('');
            }
          }
        }
      },

      // AgGrid calls this to see if component implements custom methods
      doesFilterPass() {
        return true;
      },
    };
  }, [selectedPartner, partnerList]);

  const updateFilter = useCallback(
    (partner: string) => {
      if (props.api && props.column) {
        try {
          // Get current filter model
          const currentFilterModel = props.api.getFilterModel() || {};

          // Build new filter model for this column
          const columnId = props.column.getColId();
          const newFilterModel = { ...currentFilterModel };

          if (partner) {
            // Set filter for this column
            newFilterModel[columnId] = {
              filterType: 'text',
              type: 'equals',
              filter: partner,
            };
          } else {
            // Remove filter for this column
            delete newFilterModel[columnId];
          }

          // Apply the new filter model
          props.api.setFilterModel(newFilterModel);

          // Now trigger the refresh
          if (props.api.refreshServerSide) {
            props.api.refreshServerSide({ purge: true });
          } else if (props.api.onFilterChanged) {
            props.api.onFilterChanged();
          }
          if (props.api.hideColumnFilter) {
            props.api.hideColumnFilter();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[PartnerColumnFilter] Error setting filter model:', error);
        }
      }
    },
    [props.api, props.column],
  );

  return (
    <div className="ag-filter-wrapper" style={{ width: '100%' }}>
      <div className="ag-filter-body-wrapper ag-simple-filter-body-wrapper ag-focus-managed">
        <ZfInput type="select" value="Equals" disabled getOptionLabel={() => 'Equals'}></ZfInput>
      </div>
      <ZfBox
        styles={{
          paddingLeft: 'var(--gap-horizontal-sm)',
          paddingRight: 'var(--gap-horizontal-sm)',
          paddingBottom: 'var(--gap-vertical-sm)',
        }}
      >
        <ZfInput
          name="selectedPartner"
          type="select"
          onSelectChange={(e) => {
            setSelectedPartner(e.detail as string);
            setSelectedPartnerLabel(partnerList.find((co) => co.value === (e.detail as string))?.label || '');

            setClientRepresentativeSuggestion('');
          }}
          onInputDropdownOpen={() => setClientRepresentativeSuggestion('')}
          onInputChange={(e) => setClientRepresentativeSuggestion(e.detail)}
          placeholder={t('contactNote.home.filters.placeholder.search')}
          allowSearch
          value={selectedPartner}
          getOptionLabel={(option) => {
            if (option === selectedPartner && selectedPartnerLabel) {
              return selectedPartnerLabel;
            }
            return partnerList.find((co) => co.value === option.toLowerCase())?.label || `N/A (${option})`;
          }}
        >
          {partnersLoading && (
            <div style={{ padding: '8px', textAlign: 'center' }}>
              <ZfLoadingSpinner size="extra-small" color="primary" type="infinite" />
            </div>
          )}
          {clientRepresentativeSuggestion === '' && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-xs)',
              }}
            >
              <ZfTypography variant="label-small" color="normal">
                {t('contactNote.form.startSearching')}
              </ZfTypography>
            </ZfBox>
          )}
          {clientRepresentativeSuggestion !== '' && clientRepresentativeSuggestion.length === 0 && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-md)',
              }}
            >
              <ZfTypography variant="body-small" color="normal">
                {t('contactNote.form.searchFail')}
              </ZfTypography>
            </ZfBox>
          )}
          {partnersSuccess && partnerList && partnerList.length > 0 && (
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={partnerList.length}
              overscanCount={5}
              height={Math.min(partnerList.length, 10) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(listProps: ListChildComponentProps) => {
                const { index, style } = listProps;
                const co = partnerList[index];
                return (
                  <ZfSelectOption
                    style={style}
                    key={co.value}
                    value={co.value}
                    isSelected={selectedPartner === co.value}
                  >
                    {co.label}
                  </ZfSelectOption>
                );
              }}
            </FixedSizeList>
          )}
        </ZfInput>
      </ZfBox>
      <div className="ag-filter-apply-panel">
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button ag-filter-apply-panel-apply-button"
          data-ref="applyFilterButton"
          type="submit"
          tabIndex={0}
          onClick={() => {
            updateFilter(selectedPartner);
          }}
        >
          Apply
        </button>
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          data-ref="resetFilterButton"
          type="button"
          tabIndex={0}
          onClick={() => {
            setSelectedPartner('');
            setSelectedPartnerLabel('');
            setClientRepresentativeSuggestion('');
            updateFilter('');
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
});

PartnerColumnFilter.displayName = 'PartnerColumnFilter';

export default PartnerColumnFilter;
